import { useCallback, useEffect, useState } from "react";
import {
  TrendingDown,
  RotateCcw,
  Loader2,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

/**
 * Owner-only drift £ breakdown panel.
 *
 *  - Global headline mirrors the public Trust Centre stat (same anchor).
 *  - Per-tenant table breaks the figure down by customer with each
 *    tenant's own anchor.
 *  - Per-row + global "Reset anchor" buttons let the Owner move counting
 *    forward without deleting events.
 */
const GBP = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
  maximumFractionDigits: 0,
});

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

export function DriftSavingsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/owner/drift-savings");
      setData(d);
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e?.response?.data?.detail) ||
          "Failed to load drift £ rollup",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const resetAnchor = useCallback(
    async (scope, label) => {
      if (
        !window.confirm(
          `Reset the "drift £ caught" counter for ${label} to today?\n\n` +
            `Existing events stay in the database for audit; they just stop counting going forward. ` +
            `This is what's surfaced on the public Trust Centre headline (for the global scope) ` +
            `and on this panel (for per-tenant scopes).`,
        )
      )
        return;
      setResetting(scope);
      try {
        await api.post("/owner/drift-savings/reset", { scope });
        toast.success(`Anchor reset for ${label}`);
        await load();
      } catch (e) {
        toast.error(
          formatApiErrorDetail(e?.response?.data?.detail) || "Reset failed",
        );
      } finally {
        setResetting(null);
      }
    },
    [load],
  );

  if (loading) {
    return (
      <section
        className="mt-6 rounded-2xl border border-slate-200 bg-white p-5"
        data-testid="drift-savings-loading"
      >
        <Loader2 className="animate-spin text-slate-400" size={18} />
      </section>
    );
  }
  if (!data) return null;

  const { global, by_tenant } = data;
  const anyEvents = (global?.events_count || 0) > 0 || by_tenant.length > 0;

  return (
    <section
      className="mt-6 rounded-2xl border border-slate-200 bg-white p-5"
      data-testid="drift-savings-panel"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <TrendingDown size={16} className="text-ek-purple" />
          <h2 className="font-display text-base font-semibold text-ek-ink">
            Drift £ caught
          </h2>
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600">
            since {formatDate(global.anchor_at)}
          </span>
        </div>
        <button
          type="button"
          onClick={() => resetAnchor("global", "the public Trust Centre headline")}
          disabled={resetting === "global"}
          className="inline-flex items-center gap-1.5 rounded-md border border-rose-300 bg-white px-2.5 py-1 text-xs font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-50"
          data-testid="drift-savings-reset-global"
        >
          {resetting === "global" ? (
            <Loader2 size={12} className="animate-spin" />
          ) : (
            <RotateCcw size={12} />
          )}
          Reset global anchor
        </button>
      </div>

      <p className="mt-1 text-xs text-ek-ink/70">
        Gross programme budget-at-risk surfaced by Drift Watches. Mirrors the
        public Trust Centre stat. Reset moves the counting start forward; old
        events stay in the database.
      </p>

      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Tile
          label="Total caught"
          value={GBP.format(global.total_gbp_caught || 0)}
          testid="drift-savings-total"
        />
        <Tile
          label="Alerts dispatched"
          value={global.events_count || 0}
          testid="drift-savings-events"
        />
        <Tile
          label="GBP-counted"
          value={global.gbp_events_count || 0}
          testid="drift-savings-gbp-events"
        />
        <Tile
          label="Non-GBP excluded"
          value={global.unmapped_events || 0}
          warn={global.unmapped_events > 0}
          testid="drift-savings-unmapped"
        />
      </div>

      {!anyEvents && (
        <p
          className="mt-4 rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-ek-ink/65"
          data-testid="drift-savings-empty"
        >
          No drift watches have fired since the anchor. As Drift Watches
          dispatch alerts, the £ at-risk on each programme rolls up here.
        </p>
      )}

      {by_tenant.length > 0 && (
        <div className="mt-5 overflow-x-auto">
          <table
            className="min-w-full text-xs"
            data-testid="drift-savings-by-tenant"
          >
            <thead className="text-ek-ink/60">
              <tr>
                <th className="py-1.5 pr-3 text-left font-medium">Tenant</th>
                <th className="px-3 text-right font-medium">£ caught</th>
                <th className="px-3 text-right font-medium">Alerts</th>
                <th className="px-3 text-right font-medium">Non-GBP</th>
                <th className="px-3 text-left font-medium">Since</th>
                <th className="pl-3" />
              </tr>
            </thead>
            <tbody>
              {by_tenant.map((row) => {
                const scope = `tenant:${row.tenant_id}`;
                const isResetting = resetting === scope;
                return (
                  <tr
                    key={row.tenant_id}
                    className="border-t border-slate-100"
                    data-testid={`drift-savings-row-${row.tenant_id}`}
                  >
                    <td className="py-2 pr-3 font-medium text-ek-ink">
                      {row.tenant_name}
                    </td>
                    <td className="px-3 text-right font-display font-semibold text-ek-ink">
                      {GBP.format(row.total_gbp_caught)}
                    </td>
                    <td className="px-3 text-right text-ek-ink/70">
                      {row.events_count}
                    </td>
                    <td className="px-3 text-right">
                      {row.unmapped_events > 0 ? (
                        <span className="inline-flex items-center gap-1 text-amber-700">
                          <AlertTriangle size={11} />
                          {row.unmapped_events}
                        </span>
                      ) : (
                        <span className="text-ek-ink/40">0</span>
                      )}
                    </td>
                    <td className="px-3 text-ek-ink/65">
                      {formatDate(row.anchor_at)}
                    </td>
                    <td className="py-2 pl-3 text-right">
                      <button
                        type="button"
                        onClick={() => resetAnchor(scope, row.tenant_name)}
                        disabled={isResetting}
                        className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-0.5 text-[10.5px] font-medium text-ek-ink hover:bg-slate-50 disabled:opacity-50"
                        data-testid={`drift-savings-reset-${row.tenant_id}`}
                        title="Reset this tenant's counter to today"
                      >
                        {isResetting ? (
                          <Loader2 size={10} className="animate-spin" />
                        ) : (
                          <RotateCcw size={10} />
                        )}
                        Reset
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

const Tile = ({ label, value, warn, testid }) => (
  <div
    className={`rounded-md border p-3 ${
      warn ? "border-amber-200 bg-amber-50" : "border-slate-200 bg-white"
    }`}
    data-testid={testid}
  >
    <p className="font-display text-lg font-semibold text-ek-ink">{value}</p>
    <p className="text-[10px] uppercase tracking-wide text-ek-ink/60">
      {label}
    </p>
  </div>
);
